import * as Sentry from '@sentry/react';

/**
 * Error tracking.
 *
 * Called once, as early as possible in main.tsx — errors thrown before this runs
 * are not captured, so it goes ahead of the app and of any other setup that could
 * itself throw.
 *
 * Inert without VITE_SENTRY_DSN, which is the correct behaviour for local
 * development: no accidental reporting of errors from a developer's machine, and
 * no configuration needed to run the app.
 *
 * Two things to know if you change this:
 *
 *  1. VITE_* variables are baked in at BUILD time, not read at runtime. Staging and
 *     production are separate builds, so each gets its own value — and a build made
 *     against the wrong .env reports to the wrong environment.
 *
 *  2. The Content-Security-Policy in nginx/conf.d/strymhub.conf must allow
 *     https://*.sentry.io in connect-src. Without it the browser blocks every
 *     report and Sentry appears to work while receiving nothing at all.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) return;

  Sentry.init({
    dsn,

    // Distinguishes staging from production. Both are production *builds*, so
    // NODE_ENV cannot tell them apart — this is set per environment in .env.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',

    // 10% of transactions. Enough to spot slow pages without exhausting a free-tier
    // quota, which a busy streaming frontend would do quickly at 100%.
    tracesSampleRate: 0.1,

    // Session replay is deliberately NOT enabled. It records what users do on the
    // page, which is a data-protection question this product has not answered yet,
    // and a bigger one once payments exist.

    beforeSend(event) {
      // Belt and braces over Sentry's own scrubbing. Tokens live in sessionStorage
      // and are sent as Authorization headers, and an event carrying one would put
      // a working credential into a third-party service — where it cannot be
      // revoked, because this application has no token revocation yet.
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
        delete event.request.headers.Cookie;
        delete event.request.headers.cookie;
      }

      // Query strings routinely carry reset tokens and verification codes.
      if (event.request?.query_string) {
        event.request.query_string = '[redacted]';
      }

      return event;
    },
  });
}
